import { useMemo, useState } from 'react';
import { Incident } from '../data/demoData';
import { apiFetch } from '../lib/apiClient';
import { FilterableColumnTable, type FilterableColumnDef } from './FilterableColumnTable';

interface Props {
  incident: Incident;
  customerId: string;
}

interface VulnCorrelationResult {
  ok: true;
  incidentId: string;
  customerId: string;
  generatedAt: string;
  matchedDevices: string[];
  summary: string;
  riskLevel: 'Low' | 'Medium' | 'High' | 'Critical';
  totalVulnerabilityRecords: number;
  criticalCves: number;
  highCves: number;
  publicExploitCount: number;
  activeExploitCount: number;
  kevLikeCount: number;
  internetExposureSignals: number;
  records: Record<string, unknown>[];
  cveSummary: Array<{ cve: string; severity: string; devices: string[]; exploitSignals: string[]; count: number }>;
  recommendedActions: string[];
  diagnostics: string[];
  source: 'cache' | 'defender_api' | 'log_analytics' | 'none';
}

function getIncidentId(incident: Incident): string {
  return String((incident as unknown as Record<string, unknown>).armIncidentName || incident.id || incident.incidentNumber || '').trim();
}

function csvEscape(value: unknown): string {
  const text = Array.isArray(value) ? value.join('; ') : typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function exportCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return;
  const columns = Array.from(rows.reduce((set, row) => { Object.keys(row).forEach((key) => set.add(key)); return set; }, new Set<string>()));
  const csv = [columns.join(','), ...rows.map((row) => columns.map((col) => csvEscape(row[col])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function riskClass(risk: string): string {
  if (risk === 'Critical') return 'border-red-500 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/30';
  if (risk === 'High') return 'border-orange-500 text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-950/30';
  if (risk === 'Medium') return 'border-amber-500 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30';
  return 'border-emerald-500 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/30';
}

export function IncidentVulnerabilityCorrelationPanel({ incident, customerId }: Props) {
  const incidentId = useMemo(() => getIncidentId(incident), [incident]);
  const [result, setResult] = useState<VulnCorrelationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setLoading(true); setError(null);
    try {
      const data = await apiFetch<VulnCorrelationResult>(`/incidents/${encodeURIComponent(incidentId)}/vulnerability-correlation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ customerId, incident }),
      });
      setResult(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  const columns: FilterableColumnDef<Record<string, unknown>>[] = useMemo(() => {
    const rows = result?.records ?? [];
    const preferred = ['deviceName', 'DeviceName', 'cveId', 'CveId', 'severity', 'Severity', 'vulnerabilitySeverityLevel', 'softwareName', 'SoftwareName', 'publicExploit', 'exploitInWild', 'recommendedSecurityUpdate'];
    const keys = Array.from(new Set([...preferred, ...rows.flatMap((row) => Object.keys(row))])).filter((key) => rows.some((row) => row[key] !== undefined)).slice(0, 18);
    return keys.map((key) => ({ key, header: key, headerText: key, value: (row) => row[key], render: (row) => <span className="break-words">{typeof row[key] === 'object' ? JSON.stringify(row[key]) : String(row[key] ?? '')}</span> }));
  }, [result]);

  return (
    <section className="mt-3 rounded-xl border border-orange-200 dark:border-orange-900 bg-orange-50/60 dark:bg-orange-950/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-orange-900 dark:text-orange-100">Vulnerability-to-Incident Correlation</p>
          <p className="text-xs text-orange-800/80 dark:text-orange-200/80">Correlates incident device entities with Defender Vulnerability Management exposure records, critical CVEs, exploit signals, KEV-like tags and internet exposure.</p>
        </div>
        <button onClick={run} disabled={loading} className="rounded-lg bg-orange-600 px-3 py-2 text-xs font-bold text-white hover:bg-orange-700 disabled:opacity-60">{loading ? 'Correlating…' : result ? 'Refresh Correlation' : 'Correlate Vulnerabilities'}</button>
      </div>
      {error && <div className="mt-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-2 text-xs text-red-700 dark:text-red-200">{error}</div>}
      {result && (
        <div className="mt-4 space-y-3">
          <div className={`rounded-lg border p-3 ${riskClass(result.riskLevel)}`}>
            <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-sm font-bold">{result.riskLevel} correlation risk</p><span className="text-xs font-semibold">Source: {result.source}</span></div>
            <p className="mt-1 text-xs">{result.summary}</p>
          </div>
          <div className="grid gap-3 md:grid-cols-6">
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Records</p><p className="text-lg font-bold">{result.totalVulnerabilityRecords}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Critical</p><p className="text-lg font-bold">{result.criticalCves}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">High</p><p className="text-lg font-bold">{result.highCves}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Public exploit</p><p className="text-lg font-bold">{result.publicExploitCount}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">Active exploit</p><p className="text-lg font-bold">{result.activeExploitCount}</p></div>
            <div className="rounded-lg border border-border bg-card p-3"><p className="text-[11px] uppercase text-muted-foreground">KEV-like</p><p className="text-lg font-bold">{result.kevLikeCount}</p></div>
          </div>
          <div className="rounded-lg border border-border bg-card p-3">
            <p className="text-xs font-bold">Recommended actions</p>
            <ul className="mt-2 list-disc pl-5 text-xs text-muted-foreground">{result.recommendedActions.map((a) => <li key={a}>{a}</li>)}</ul>
          </div>
          {result.cveSummary.length > 0 && (
            <div className="rounded-lg border border-border bg-card p-3">
              <p className="text-xs font-bold">Top matched CVEs</p>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {result.cveSummary.slice(0, 8).map((cve) => (
                  <div key={cve.cve} className="rounded-lg border border-border p-2 text-xs">
                    <div className="flex items-center justify-between gap-2"><span className="font-bold">{cve.cve}</span><span>{cve.severity || 'Unknown'} · {cve.count}</span></div>
                    <p className="mt-1 text-muted-foreground">{cve.devices.slice(0, 4).join(', ') || 'Device name unavailable'}</p>
                    {cve.exploitSignals.length > 0 && <p className="mt-1 text-orange-700 dark:text-orange-300">{cve.exploitSignals.join(', ')}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="mb-2 flex items-center justify-between gap-2"><p className="text-xs font-bold">Matched vulnerability records</p><button onClick={() => exportCsv(`incident-vulnerability-correlation-${incident.incidentNumber || incidentId}.csv`, result.records)} disabled={!result.records.length} className="rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-semibold hover:bg-muted disabled:opacity-50">Export CSV</button></div>
            <FilterableColumnTable rows={result.records} columns={columns} storageKey={`incident-vuln-correlation-${incidentId}`} rowKey={(_, idx) => idx} emptyMessage="No matching vulnerability records." maxHeightClassName="max-h-[520px]" />
          </div>
          {result.diagnostics.length > 0 && <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-2 text-xs text-amber-800 dark:text-amber-200">{result.diagnostics.join(' ')}</div>}
        </div>
      )}
    </section>
  );
}
